<template>
  <span>
    <b-row class="mb-4 mt-2">
      <b-col class="geolocation-report-header">
        Geotargeting report
        <tooltip
          id="geolocation-report-tooltip"
          tooltip-text="Geolocations your campaign was displayed from."
        />
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col class="col-12 col-md-6 col-lg-4 py-md-1 py-2">
        <query-param-button-group
          :options="[
            {
              label: 'Region',
              value: 'region',
            },
            {
              label: 'City',
              value: 'city',
            },
          ]"
          :value="groupBy"
          name="geogroupby"
          class="w-100"
          @input="setGroupBy"
        />
      </b-col>
    </b-row>
    <div>
      <b-card>
        <div class="geo-type-header mt-2 mb-3">{{ geoTypeText }}</div>
        <b-table-simple responsive>
          <b-thead>
            <b-tr>
              <b-th
                v-if="groupBy === 'CITY'"
                :aria-sort="sortings.city"
                sticky-column
                @click="sortHandler('city')"
                >City</b-th
              >
              <b-th
                v-if="['CITY', 'REGION'].includes(groupBy)"
                :aria-sort="sortings.state"
                :sticky-column="groupBy === 'REGION'"
                @click="sortHandler('state')"
                >Region</b-th
              >
              <b-th
                :aria-sort="sortings.country"
                :sticky-column="groupBy === 'COUNTRY'"
                @click="sortHandler('country')"
                >Country</b-th
              >
              <b-th
                :aria-sort="sortings.impressions"
                @click="sortHandler('impressions')"
                >Impressions</b-th
              >
              <b-th :aria-sort="sortings.clicks" @click="sortHandler('clicks')"
                >Clicks</b-th
              >
              <b-th :aria-sort="sortings.ctr" @click="sortHandler('ctr')"
                >CTR</b-th
              >
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="location in geolocations"
              :key="location.city || location.state || location.country"
            >
              <b-td
                v-if="groupBy === 'CITY'"
                class="text-capitalize"
                sticky-column
              >
                <span v-if="location.city !== 'other'">
                  {{ location.city }}
                </span>
                <span
                  v-if="
                    groupBy === 'CITY' &&
                    location.city &&
                    location.city === 'other'
                  "
                >
                  Other / Unknown
                  <span id="tooltipother" class="ml-2 help">
                    <fa-icon :icon="['far', 'circle-question']" size="xs" />
                  </span>
                  <b-popover
                    target="tooltipother"
                    triggers="hover focus"
                    placement="top"
                  >
                    Cities that are not on the top list or could not be
                    identified.
                  </b-popover>
                </span>
              </b-td>
              <!-- region column -->
              <b-td
                v-if="['CITY', 'REGION'].includes(groupBy)"
                :sticky-column="groupBy === 'REGION'"
                class="text-capitalize"
              >
                <span
                  v-if="
                    groupBy === 'REGION' &&
                    location.state &&
                    location.state === 'other'
                  "
                >
                  Other / Unknown
                  <span id="tooltipother" class="ml-2 help">
                    <fa-icon :icon="['far', 'circle-question']" size="xs" />
                  </span>
                  <b-popover
                    target="tooltipother"
                    triggers="hover focus"
                    placement="top"
                  >
                    Regions that are not on the top list or could not be
                    identified.
                  </b-popover>
                </span>
                <span v-if="location.state !== 'other' && groupBy === 'CITY'">
                  {{ location.state }}
                </span>
                <span v-if="location.state !== 'other' && groupBy === 'REGION'">
                  {{ location.state }}
                </span>
              </b-td>
              <!-- country column -->
              <b-td class="text-capitalize">
                <span v-if="location.country !== 'other'">
                  {{ location.country }}
                </span>
              </b-td>
              <b-td>
                {{ location.impressions | thousandsFormatter }}
              </b-td>
              <b-td>
                {{ location.clicks | thousandsFormatter }}
              </b-td>
              <b-td>{{ location.ctr | twoDecimalsFormatter }}%</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
        <div
          v-if="$apollo.queries.geolocations.loading"
          class="text-center my-2 text-secondary w-100"
        >
          <b-spinner class="align-middle" />
          <strong>Loading...</strong>
        </div>
      </b-card>
    </div>
  </span>
</template>

<script>
import Vue from 'vue';
import gql from 'graphql-tag';
import QueryParamButtonGroup from '~shared/components/selectors/QueryParamButtonGroup.vue';
import Tooltip from '~shared/components/generic/Tooltip.vue';
import { normalizeDateRange, setSortColumn } from '~shared/utils/index.js';

// Handle miss match between UI values and API values
const getGeoGroupByEnum = (val) => {
  if (!val) {
    return '';
  }
  if (val.toUpperCase() === 'REGION') {
    return 'STATE';
  }
  return val.toUpperCase();
};

export default Vue.extend({
  components: {
    QueryParamButtonGroup,
    Tooltip,
  },
  props: {
    includeArchivedCampaigns: {
      type: Boolean,
      default: false,
    },
    isNoData: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      startDate: '',
      endDate: '',
      groupBy: 'REGION',
      sortings: {
        city: 'none',
        state: 'none',
        country: 'none',
        impressions: 'none',
        clicks: 'none',
        ctr: 'none',
      },
      currentSorting: {
        column: 'impressions',
        direction: 'DESC',
      },
    };
  },
  computed: {
    campaignId() {
      const selectedCampaign =
        this.$store.state['advertiser-insights'].filters.selectedCampaign;
      if (!selectedCampaign || !selectedCampaign.id) {
        return null;
      }
      return selectedCampaign.id;
    },
    groupId() {
      const selectedGroup =
        this.$store.state['advertiser-insights'].filters.selectedGroup;
      if (!selectedGroup || !selectedGroup.id) {
        return null;
      }
      return selectedGroup.id;
    },
    selectedDateRange() {
      return this.$store.state['advertiser-insights'].filters.selectedDateRange;
    },
    geoTypeText() {
      switch (this.groupBy) {
        case 'REGION':
          return 'Regions - Top 10';
        case 'CITY':
          return 'Cities - Top 50';
      }
    },
  },
  watch: {
    selectedDateRange: {
      handler(val) {
        if (val.length === 2) {
          const { startDate, endDate } = normalizeDateRange({
            startDate: val[0],
            endDate: val[1],
          });

          this.startDate = startDate.toISOString();
          this.endDate = endDate.toISOString();
        }
      },
      deep: true,
      immediate: true,
    },
    '$route.query': {
      handler(val) {
        const geoSort = val['geo-sort'];
        const geoOrder = val['geo-order'];
        // Act only on browser actions (back/forward)
        if (geoSort && geoOrder && !this.isQuerySynced()) {
          const { currentColumn, currentDirection } = setSortColumn(
            geoSort,
            this.sortings,
            geoOrder.toLowerCase()
          );
          this.currentSorting = {
            column: currentColumn ? currentColumn.toLowerCase() : 'impressions',
            direction: currentDirection,
          };
          this.sortList(this.geolocations);
        }
      },
    },
  },
  mounted() {
    const column = this.$route.query['geo-sort'];
    const direction = this.$route.query['geo-order'];
    if (column && direction) {
      this.currentSorting = {
        column,
        direction,
      };
      this.sortings[column] = direction === 'DESC' ? 'descending' : 'ascending';
    }
  },
  methods: {
    sortList(list = []) {
      if (list.length === 0) {
        return list;
      }
      let other = null;
      // Find the "other" record and remove it from the list. We will add it back later
      const otherIdx = list.findIndex((s) => s.state === 'other');
      if (otherIdx !== -1) {
        other = list.splice(otherIdx, 1)[0];
      }
      const c = this.currentSorting.column;
      const o = this.currentSorting.direction;
      const sort = (a, b) => {
        if (a[c] < b[c]) {
          return o === 'ASC' ? -1 : 1;
        } else if (a[c] > b[c]) {
          return o === 'ASC' ? 1 : -1;
        }
        return 0;
      };
      list.sort(sort);
      if (other) {
        list.push(other);
      }
      return list;
    },
    isQuerySynced() {
      const { column, direction } = this.currentSorting;
      const geoSort = this.$route.query['geo-sort'];
      const geoOrder = this.$route.query['geo-order'];

      return column === geoSort && direction === geoOrder;
    },
    // Used then clicking table headings
    sortHandler(column) {
      const sort = setSortColumn(column, this.sortings);
      // If column is null, fall back to sorting on impressions
      this.currentSorting = {
        column: sort.currentColumn
          ? sort.currentColumn.toLowerCase()
          : 'impressions',
        direction: sort.currentDirection,
      };
      if (!this.isQuerySynced()) {
        this.$router.push({
          query: Object.assign({}, this.$route.query, {
            'geo-sort': this.currentSorting.column,
            'geo-order': this.currentSorting.direction,
          }),
        });
      }
      this.sortList(this.geolocations);
    },
    setGroupBy(val) {
      if (!val) {
        this.groupBy = 'REGION';
      } else {
        this.groupBy = val.toUpperCase();
      }
    },
    resetSortings() {
      Object.keys(this.sortings).forEach((key) => {
        this.sortings[key] = 'none';
      });
      this.currentSorting = { column: 'impressions', direction: 'DESC' };
      if (!this.isQuerySynced()) {
        this.$router.push({
          query: Object.assign({}, this.$route.query, {
            'geo-sort': undefined,
            'geo-order': undefined,
          }),
        });
      }
    },
  },
  apollo: {
    geolocations: {
      query: gql`
        query CampaignGeolocationStatistics(
          $groupIds: [ID!]
          $campaignId: ID!
          $startDate: DateTime!
          $endDate: DateTime!
          $groupBy: CampaignGeolocationStatisticsGroupBy
          $includeArchivedCampaigns: Boolean
        ) {
          geolocations: CampaignGeolocationStatistics(
            groupIds: $groupIds
            campaignId: $campaignId
            startDate: $startDate
            endDate: $endDate
            groupBy: $groupBy
            includeArchivedCampaigns: $includeArchivedCampaigns
          ) {
            statistics {
              country
              state
              city
              impressions
              clicks
              ctr
            }
          }
        }
      `,
      variables() {
        const campaignId = this.campaignId || '';
        const groupIds = [this.groupId];
        return {
          startDate: this.startDate,
          endDate: this.endDate,
          groupIds,
          campaignId,
          groupBy: getGeoGroupByEnum(this.groupBy),
          includeArchivedCampaigns: this.includeArchivedCampaigns,
        };
      },
      skip() {
        return !this.groupId;
      },
      update(data) {
        if (data.geolocations && data.geolocations.statistics) {
          const stats = data.geolocations.statistics;
          // Declare this just in case it's missing,  N/A's will need this later
          let other = {
            country: 'other',
            state: 'other',
            city: 'other',
            impressions: 0,
            clicks: 0,
            ctr: 0,
          };

          const i = stats.findIndex((s) => s.country === 'other');
          if (i !== -1) {
            other = stats.splice(i, 1)[0];
          }

          // Find and remove all N/A from list and add them to unknown statistics
          const naIndexes = [];
          stats.forEach((s, index) => {
            if (s.country === 'N/A' || s.state === 'N/A' || s.city === 'N/A') {
              naIndexes.push(index);
            }
          });
          // Reverse the array to make the indexes removable
          naIndexes.reverse();
          naIndexes.forEach((index) => {
            // Add N/A statistics
            other.impressions += stats[index].impressions;
            other.clicks += stats[index].clicks;
            other.ctr = other.impressions
              ? (other.clicks / other.impressions) * 100
              : 0;
            // Remove current N/A row from statistics
            stats.splice(index, 1);
          });

          // Honor the current sort settings
          this.sortList(stats);

          // Put the unknown statistics at the end of the list
          if (other.impressions) {
            stats.push(other);
          }

          // If the selected groupBy is hiding the currently sorted column, reset sorting
          if (
            (this.groupBy === 'COUNTRY' &&
              ['state', 'city'].includes(this.currentSorting.column)) ||
            (this.groupBy === 'REGION' && this.currentSorting.column === 'city')
          ) {
            this.resetSortings();
          }

          return stats;
        }
      },
      fetchPolicy: 'cache-first',
      debounce: 200,
      notifyOnNetworkStatusChange: true,
    },
  },
});
</script>

<style scoped>
.geolocation-report-header {
  font-size: 18px;
  font-weight: 600;
}
.geo-type-header {
  font-size: 14px;
  font-weight: 600;
}
</style>
